import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { isIndexedDbConnectionError } from '@/shared/lib/queryErrorTracking';

// Configuration constants
const SENTRY_CONFIG = {
  DSN: 'https://8909fb2b0ca421e67d747c29dc427694@o4508460976635904.ingest.us.sentry.io/4508460981747712',
  TRACE_SAMPLE_RATE: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 1.0,
} as const;

const IGNORED_ERRORS = [
  // Browser extensions
  'chrome-extension://',
  'moz-extension://',
  // Common browser errors
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Network errors that are expected
  'NetworkError',
  'Failed to fetch',
  // User-initiated cancellations
  'AbortError',
  // Firestore internal errors (SDK issues we cannot control)
  'FIRESTORE (10.14.1) INTERNAL ASSERTION FAILED',
  'FIRESTORE INTERNAL ASSERTION FAILED',
] as const;

/**
 * Apply custom fingerprinting to group similar errors
 */
function applyCustomFingerprinting(event: Sentry.Event, error: Error): void {
  // Group iOS IndexedDB connection errors together
  // These are transient errors caused by iOS killing background connections
  if (isIndexedDbConnectionError(error)) {
    event.fingerprint = ['indexeddb', 'connection-lost'];
    event.level = 'warning';
    return;
  }

  // Group Firestore internal assertion errors
  if (error.message?.includes('FIRESTORE') && error.message?.includes('INTERNAL ASSERTION FAILED')) {
    event.fingerprint = ['firestore', 'internal-assertion'];
    event.level = 'warning';
    return;
  }

  // Group Firebase permission errors
  if (error.message?.includes('Missing or insufficient permissions')) {
    event.fingerprint = ['firebase', 'permission-denied'];
    return;
  }

  // Group network timeout errors
  const isNetworkTimeout =
    error.message?.includes('timeout') ||
    error.message?.includes('Network request failed');

  if (isNetworkTimeout) {
    event.fingerprint = ['network', 'timeout'];
  }
}

/**
 * Remove sensitive data from error reports
 */
function sanitizeEvent(event: Sentry.Event): void {
  // Remove all cookie data
  if (event.request?.cookies) {
    delete event.request.cookies;
  }

  // Mask email local part while keeping domain for debugging
  if (event.user?.email) {
    const [, domain] = event.user.email.split('@');
    if (domain) {
      event.user.email = `***@${domain}`;
    }
  }
}

/**
 * Determine if error should be filtered out in development
 */
function shouldFilterInDevelopment(event: Sentry.Event, isDevelopment: boolean): boolean {
  return isDevelopment && event.exception?.values?.[0]?.type === 'NetworkError';
}

/**
 * 로컬·CI E2E의 Firebase 오류(가짜 키 등)가 운영 프로젝트로 유입되지 않게 걸러낸다.
 */
export function isDevelopmentFirebaseError(event: Sentry.Event): boolean {
  return (
    event.environment === 'development' &&
    event.exception?.values?.[0]?.type === 'FirebaseError'
  );
}

/**
 * Initialize Sentry with comprehensive error tracking configuration
 */
export const initSentry = (): void => {
  const isDevelopment = import.meta.env.DEV;
  const environment = isDevelopment ? 'development' : 'production';

  Sentry.init({
    dsn: SENTRY_CONFIG.DSN,
    environment,
    sendDefaultPii: true,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        // Web Vitals (LCP/FCP/CLS) are captured by PerformanceObserver,
        // so fetch/XHR span instrumentation stays off to avoid critical-path cost.
        traceFetch: false,
        traceXHR: false,
      }),
    ],
    tracesSampleRate: SENTRY_CONFIG.TRACE_SAMPLE_RATE,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/daily-writing-friends\.com\/api/,
      import.meta.env.VITE_SUPABASE_URL,
    ],
    ignoreErrors: [...IGNORED_ERRORS],

    beforeSend(event, hint) {
      // Filter out development network errors
      if (shouldFilterInDevelopment(event, isDevelopment)) {
        return null;
      }

      if (isDevelopmentFirebaseError(event)) {
        return null;
      }

      const error = hint.originalException;
      if (error instanceof Error) {
        applyCustomFingerprinting(event, error);
      }

      sanitizeEvent(event);

      return event;
    },
  });
};

/**
 * Set user context for Sentry error tracking
 */
export const setSentryUser = (
  user: { uid: string; email?: string | null; displayName?: string | null } | null,
) => {
  if (user) {
    Sentry.setUser({
      id: user.uid,
      email: user.email || undefined,
      username: user.displayName || undefined,
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Add breadcrumb for user actions
 */
export const addSentryBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Set context for current feature/page
 */
export const setSentryContext = (key: string, context: Record<string, unknown>) => {
  Sentry.setContext(key, context);
};

/**
 * Set tags for filtering and searching
 */
export const setSentryTags = (tags: Record<string, string>) => {
  Sentry.setTags(tags);
};

export const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

